import React, { useCallback, useEffect, useState } from 'react';
import Input from '../../components/ui/Input';
import { getApiErrorMessage } from '../../utils/errors';
import {
  createWhatsappCatalogMetaTemplate,
  createWhatsappCatalogSlot,
  listWhatsappCatalogAdmin,
  listWhatsappCatalogMetaTemplates,
  updateWhatsappCatalogSlot,
} from '../../api/whatsappCatalogAdmin';
import { buildSlotPayload } from './formUtils';

const emptyForm = {
  id: '',
  slug: '',
  label: '',
  provider: 'botmaker',
  botmaker_channel_id: '',
  phone_number_id: '',
  waba_id: '',
  token: '',
  api_host: '',
  phone_number: '',
  sort_order: '0',
  is_active: false,
};

const emptyTemplateForm = {
  name: '',
  language: 'pt_BR',
  category: 'UTILITY',
  body: '',
  exampleValues: '',
  quickReplies: [],
};

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${
        active
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
          : 'bg-slate-100 text-slate-600 border border-slate-200'
      }`}
    >
      {active ? 'Ativo' : 'Inativo'}
    </span>
  );
}

function WhatsappCatalogAdmin() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [tokenConfigured, setTokenConfigured] = useState(false);
  const [tokenEditing, setTokenEditing] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [templateSuccess, setTemplateSuccess] = useState('');
  const [templateForm, setTemplateForm] = useState(emptyTemplateForm);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await listWhatsappCatalogAdmin();
      setEntries(response.data || []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar o catálogo WhatsApp.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const loadMetaTemplates = useCallback(async (slotId) => {
    setTemplatesLoading(true);
    setTemplateError('');
    try {
      const response = await listWhatsappCatalogMetaTemplates(slotId);
      setTemplates(response.data || []);
    } catch (err) {
      setTemplates([]);
      setTemplateError(
        getApiErrorMessage(err, 'Não foi possível carregar os templates da Meta.')
      );
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const resetTemplateState = () => {
    setTemplates([]);
    setTemplateForm(emptyTemplateForm);
    setTemplateError('');
    setTemplateSuccess('');
  };

  const resetForm = () => {
    setForm(emptyForm);
    setTokenConfigured(false);
    setTokenEditing(true);
    resetTemplateState();
  };

  const handleSelect = (entry) => {
    resetTemplateState();
    if ((entry.provider || 'botmaker') === 'meta') {
      loadMetaTemplates(entry.id);
    }
    setForm({
      id: entry.id,
      slug: entry.slug,
      label: entry.label || '',
      provider: entry.provider || 'botmaker',
      botmaker_channel_id: entry.botmaker_channel_id || '',
      phone_number_id: entry.phone_number_id || '',
      waba_id: entry.waba_id || '',
      token: '',
      api_host: entry.api_host || '',
      phone_number: entry.phone_number || '',
      sort_order: String(entry.sort_order ?? 0),
      is_active: Boolean(entry.is_active),
    });
    setTokenConfigured(Boolean(entry.token_configured));
    setTokenEditing(!entry.token_configured);
    setSuccess('');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    const payload = buildSlotPayload(form, { tokenEditing });

    try {
      if (form.id) {
        const updated = await updateWhatsappCatalogSlot(form.id, payload);
        setSuccess(
          payload.token ? 'Slot e senha atualizados.' : 'Slot atualizado. Senha mantida.'
        );
        setForm((prev) => ({ ...prev, token: '' }));
        setTokenConfigured(Boolean(updated.token_configured));
        setTokenEditing(!updated.token_configured);
      } else {
        await createWhatsappCatalogSlot({
          slug: form.slug.trim().toLowerCase(),
          ...payload,
        });
        setSuccess('Slot criado.');
        resetForm();
      }
      await loadEntries();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível salvar o slot.'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTemplate = async (event) => {
    event.preventDefault();
    if (!form.id) return;

    setTemplateSaving(true);
    setTemplateError('');
    setTemplateSuccess('');
    const exampleValues = templateForm.exampleValues
      .split('\n')
      .map((value) => value.trim())
      .filter(Boolean);
    const quickReplies = templateForm.quickReplies
      .map((value) => value.trim())
      .filter(Boolean);

    try {
      await createWhatsappCatalogMetaTemplate(form.id, {
        name: templateForm.name.trim().toLowerCase(),
        language: templateForm.language,
        category: templateForm.category,
        body: templateForm.body.trim(),
        example_values: exampleValues,
        quick_replies: quickReplies,
      });
      setTemplateSuccess('Template enviado para análise da Meta.');
      setTemplateForm(emptyTemplateForm);
      await loadMetaTemplates(form.id);
    } catch (err) {
      setTemplateError(
        getApiErrorMessage(err, 'Não foi possível criar o template na Meta.')
      );
    } finally {
      setTemplateSaving(false);
    }
  };

  const isMeta = form.provider === 'meta';
  const showTemplates = isMeta && Boolean(form.id);
  const tokenLabel = isMeta ? 'Access token Meta' : 'Token';

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-content-primary">Catálogo WhatsApp</h1>
        <p className="text-sm text-content-muted mt-1">
          Gerencie slots de canal WhatsApp disponíveis para os agentes.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-emerald-600">{success}</p>}

      <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <section className="surface-card p-6 shadow-card-md space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-content-primary">Slots</h2>
            <button type="button" className="btn-secondary text-sm" onClick={resetForm}>
              Novo
            </button>
          </div>
          {loading ? (
            <p className="text-sm text-content-muted">Carregando...</p>
          ) : (
            <ul className="divide-y divide-slate-200">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(entry)}
                    className="w-full py-3 text-left hover:text-forge-600 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium text-content-primary">{entry.label}</p>
                      <StatusBadge active={entry.is_active} />
                    </div>
                    <p className="text-xs text-content-muted">
                      {entry.slug} · {entry.provider || 'botmaker'} ·{' '}
                      {entry.phone_number || 'sem telefone'} ·{' '}
                      {entry.token_configured ? 'senha configurada' : 'sem senha'}
                    </p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="surface-card p-6 shadow-card-md">
          <h2 className="text-lg font-semibold text-content-primary mb-4">
            {form.id ? `Editar: ${form.slug}` : 'Novo slot'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              id="slug"
              label="Slug"
              value={form.slug}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              disabled={Boolean(form.id)}
              required
            />
            <Input
              id="label"
              label="Label"
              value={form.label}
              onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
              required
            />
            <div>
              <label htmlFor="provider" className="block text-sm font-medium text-content-primary mb-1">
                Provider
              </label>
              <select
                id="provider"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={form.provider}
                onChange={(e) => setForm((prev) => ({ ...prev, provider: e.target.value }))}
              >
                <option value="botmaker">Botmaker</option>
                <option value="meta">Meta Cloud API</option>
              </select>
            </div>
            {!isMeta && (
              <Input
                id="botmaker_channel_id"
                label="Botmaker channel ID"
                value={form.botmaker_channel_id}
                onChange={(e) => setForm((prev) => ({ ...prev, botmaker_channel_id: e.target.value }))}
                required={!form.id}
              />
            )}
            {isMeta && (
              <>
                <Input
                  id="phone_number_id"
                  label="Meta phone number ID"
                  value={form.phone_number_id}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, phone_number_id: e.target.value }))
                  }
                  required={!form.id}
                />
                <Input
                  id="waba_id"
                  label="WhatsApp Business Account ID (WABA)"
                  value={form.waba_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, waba_id: e.target.value }))}
                  hint="Necessário para listar e criar templates pela Meta."
                />
              </>
            )}
            {tokenConfigured && !tokenEditing ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3">
                <p className="text-sm font-medium text-content-primary">
                  {tokenLabel}: configurado
                </p>
                <p className="text-xs text-content-muted mt-1">
                  Atualizar o slot não altera a senha guardada. Só clique em trocar se
                  for realmente substituir a credencial.
                </p>
                <button
                  type="button"
                  className="btn-secondary text-sm mt-3"
                  onClick={() => setTokenEditing(true)}
                >
                  Trocar senha
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  id="token"
                  name="whatsapp-catalog-token"
                  autoComplete="new-password"
                  label={tokenLabel}
                  type="password"
                  value={form.token}
                  onChange={(e) => setForm((prev) => ({ ...prev, token: e.target.value }))}
                  required={!form.id}
                  hint={
                    tokenConfigured
                      ? 'Deixe vazio para manter a senha atual.'
                      : 'Este slot ainda não tem senha configurada.'
                  }
                />
                {tokenConfigured && (
                  <button
                    type="button"
                    className="text-sm text-forge-600 hover:text-forge-700"
                    onClick={() => {
                      setForm((prev) => ({ ...prev, token: '' }));
                      setTokenEditing(false);
                    }}
                  >
                    Cancelar troca de senha
                  </button>
                )}
              </div>
            )}
            {!isMeta && (
              <Input
                id="api_host"
                label="API host"
                value={form.api_host}
                onChange={(e) => setForm((prev) => ({ ...prev, api_host: e.target.value }))}
              />
            )}
            <Input
              id="phone_number"
              label="Telefone"
              value={form.phone_number}
              onChange={(e) => setForm((prev) => ({ ...prev, phone_number: e.target.value }))}
            />
            <Input
              id="sort_order"
              label="Ordem"
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((prev) => ({ ...prev, sort_order: e.target.value }))}
            />
            <label className="flex items-center gap-2 text-sm text-content-muted">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
              />
              Ativo
            </label>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Salvando...' : form.id ? 'Atualizar' : 'Criar'}
            </button>
          </form>
        </section>
      </div>

      {showTemplates && (
        <section className="surface-card p-6 shadow-card-md space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-content-primary">
              Templates WhatsApp Meta
            </h2>
            <p className="text-sm text-content-muted mt-1">
              Templates pertencem a este número da Meta e continuam valendo mesmo que
              os canais dos agentes sejam recriados.
            </p>
          </div>

          {templateError && <p className="text-sm text-red-600">{templateError}</p>}
          {templateSuccess && (
            <p className="text-sm text-emerald-700">{templateSuccess}</p>
          )}

          <form onSubmit={handleCreateTemplate} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                id="meta_template_name"
                label="Nome do template"
                value={templateForm.name}
                onChange={(event) =>
                  setTemplateForm((prev) => ({ ...prev, name: event.target.value }))
                }
                placeholder="lembrete_consulta"
                hint="Use letras minúsculas, números e _."
                required
              />
              <Input
                id="meta_template_language"
                label="Idioma"
                value={templateForm.language}
                onChange={(event) =>
                  setTemplateForm((prev) => ({ ...prev, language: event.target.value }))
                }
                placeholder="pt_BR"
                required
              />
            </div>

            <div>
              <label
                htmlFor="meta_template_category"
                className="block text-sm font-medium text-content-primary mb-1"
              >
                Categoria
              </label>
              <select
                id="meta_template_category"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={templateForm.category}
                onChange={(event) =>
                  setTemplateForm((prev) => ({ ...prev, category: event.target.value }))
                }
              >
                <option value="UTILITY">Utilidade</option>
                <option value="MARKETING">Marketing</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="meta_template_body"
                className="block text-sm font-medium text-content-primary mb-1"
              >
                Texto
              </label>
              <textarea
                id="meta_template_body"
                className="w-full min-h-28 rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={templateForm.body}
                onChange={(event) =>
                  setTemplateForm((prev) => ({ ...prev, body: event.target.value }))
                }
                placeholder="Olá {{1}}, sua consulta está marcada para {{2}}."
                maxLength={1024}
                required
              />
              <p className="text-xs text-content-muted mt-1">
                Variáveis devem ser sequenciais: {'{{1}}'}, {'{{2}}'}...
              </p>
            </div>

            <div>
              <label
                htmlFor="meta_template_examples"
                className="block text-sm font-medium text-content-primary mb-1"
              >
                Exemplos das variáveis
              </label>
              <textarea
                id="meta_template_examples"
                className="w-full min-h-20 rounded-md border border-slate-300 px-3 py-2 text-sm"
                value={templateForm.exampleValues}
                onChange={(event) =>
                  setTemplateForm((prev) => ({
                    ...prev,
                    exampleValues: event.target.value,
                  }))
                }
                placeholder={'Maria\n05/08 às 14h'}
              />
              <p className="text-xs text-content-muted mt-1">
                Um exemplo por linha, na mesma ordem das variáveis.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-content-primary">
                    Botões de resposta rápida
                  </p>
                  <p className="text-xs text-content-muted mt-1">
                    Opcional. Adicione até 3 respostas personalizadas.
                  </p>
                </div>
                {templateForm.quickReplies.length < 3 && (
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    onClick={() =>
                      setTemplateForm((prev) => ({
                        ...prev,
                        quickReplies: [...prev.quickReplies, ''],
                      }))
                    }
                  >
                    Adicionar botão
                  </button>
                )}
              </div>

              {templateForm.quickReplies.map((reply, index) => (
                <div
                  key={`quick-reply-${index}`}
                  className="flex items-end gap-2"
                >
                  <div className="flex-1">
                    <label
                      htmlFor={`meta_template_quick_reply_${index}`}
                      className="block text-sm font-medium text-content-primary mb-1"
                    >
                      Botão {index + 1}
                    </label>
                    <input
                      id={`meta_template_quick_reply_${index}`}
                      className="field-input"
                      value={reply}
                      maxLength={25}
                      onChange={(event) =>
                        setTemplateForm((prev) => ({
                          ...prev,
                          quickReplies: prev.quickReplies.map((value, replyIndex) =>
                            replyIndex === index ? event.target.value : value
                          ),
                        }))
                      }
                      placeholder="Ex.: Confirmar"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    onClick={() =>
                      setTemplateForm((prev) => ({
                        ...prev,
                        quickReplies: prev.quickReplies.filter(
                          (_, replyIndex) => replyIndex !== index
                        ),
                      }))
                    }
                  >
                    Remover
                  </button>
                </div>
              ))}
            </div>

            <button
              type="submit"
              disabled={templateSaving}
              className="btn-primary text-sm disabled:opacity-40"
            >
              {templateSaving ? 'Enviando...' : 'Criar template na Meta'}
            </button>
          </form>

          <div>
            <div className="flex items-center justify-between gap-3 mb-2">
              <h3 className="text-sm font-semibold text-content-primary">
                Templates cadastrados
              </h3>
              <button
                type="button"
                className="text-sm text-forge-600 hover:text-forge-700"
                onClick={() => loadMetaTemplates(form.id)}
                disabled={templatesLoading}
              >
                Atualizar
              </button>
            </div>
            {templatesLoading ? (
              <p className="text-sm text-content-muted">Carregando...</p>
            ) : templates.length === 0 ? (
              <p className="text-sm text-content-muted">Nenhum template encontrado.</p>
            ) : (
              <ul className="divide-y divide-slate-200 border-y border-slate-200">
                {templates.map((template) => (
                  <li
                    key={template.id || `${template.name}-${template.language}`}
                    className="py-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-content-primary">
                          {template.name}
                        </p>
                        <p className="text-xs text-content-muted">
                          {template.language} · {template.category}
                        </p>
                      </div>
                      <span className="inline-flex rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-content-muted">
                        {template.status || 'PENDENTE'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default WhatsappCatalogAdmin;
